import React, { useEffect, useRef, useState } from 'react';
import GroupActions from '../components/GroupActions';
import ReviewModal from '../components/ReviewModal';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import './ChatRoomPage.css';
import { API_BASE_URL } from '../api/config';


function ChatRoomPage({ chatRoomId, chatRoomName, username, onBack, onViewProfile }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [postInfo, setPostInfo] = useState(null);
  const [groupInfo, setGroupInfo] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);
  const clientRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (!chatRoomId) return;
    fetch(`${API_BASE_URL}/api/chatrooms/${chatRoomId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data && data.post) {
          setPostInfo(data.post);
          if (data.post.groupId) {
            fetchGroupInfo(data.post.groupId);
            fetchParticipants(data.post.groupId);
          }
        } else {
          setPostInfo(null);
        }
      })
      .catch(() => setPostInfo(null));
  }, [chatRoomId]);

  const fetchGroupInfo = (groupId) => {
    fetch(`${API_BASE_URL}/api/groups/${groupId}`)
      .then((res) => res.json())
      .then((data) => setGroupInfo(data))
      .catch(() => setGroupInfo(null));
  };

  const fetchParticipants = (groupId) => {
    fetch(`${API_BASE_URL}/api/groups/${groupId}/participants`)
      .then((res) => res.json())
      .then((data) => setParticipants(Array.isArray(data) ? data.map((p) => p.username) : []))
      .catch(() => setParticipants([]));
  };

  const handleGroupUpdate = () => {
    if (postInfo?.groupId) {
      fetchGroupInfo(postInfo.groupId);
      fetch(`${API_BASE_URL}/api/groups/${postInfo.groupId}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.status === 'COMPLETED') setShowReviewModal(true);
        });
    }
  };

  useEffect(() => {
    if (!chatRoomId) return;
    fetch(`${API_BASE_URL}/api/chatrooms/${chatRoomId}/messages`)
      .then((res) => res.json())
      .then((data) => setMessages(Array.isArray(data) ? data : []))
      .catch(() => setMessages([]));

    const client = new Client({
      brokerURL: undefined,
      webSocketFactory: () => new SockJS(`${API_BASE_URL}/ws`),
      reconnectDelay: 5000,
      onConnect: () => {
        client.subscribe(`/topic/chat/${chatRoomId}`, (msg) => {
          try {
            setMessages((prev) => [...prev, JSON.parse(msg.body)]);
          } catch (e) {
            /* ignore */
          }
        });
      },
    });
    client.activate();
    clientRef.current = client;

    return () => {
      clientRef.current?.deactivate();
      clientRef.current = null;
    };
  }, [chatRoomId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!clientRef.current || !chatRoomId) return;

    if (imageFile) {
      const formData = new FormData();
      formData.append('sender', username);
      formData.append('content', input || '');
      formData.append('image', imageFile);
      try {
        await fetch(`${API_BASE_URL}/api/chatrooms/${chatRoomId}/messages/upload`, {
          method: 'POST',
          body: formData,
        });
        setInput('');
        setImageFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch (e) {
        console.error('Image upload failed:', e);
      }
      return;
    }

    if (!input.trim()) return;
    clientRef.current.publish({
      destination: `/app/chat/${chatRoomId}`,
      body: JSON.stringify({ sender: username, content: input }),
    });
    setInput('');
  };

  const handleLeaveChatRoom = () => {
    if (!window.confirm('정말 채팅방에서 나가시겠습니까?\n나가면 다시 입장할 수 없습니다.')) {
      return;
    }
    fetch(`${API_BASE_URL}/api/chatrooms/${chatRoomId}/leave?username=${username}`, {
      method: 'DELETE',
    })
      .then((res) => res.json())
      .then((data) => {
        alert(data.message || '채팅방에서 나갔습니다.');
        onBack();
      })
      .catch(() => alert('채팅방 나가기에 실패했습니다.'));
  };

  const statusClass =
    groupInfo?.status === 'COMPLETED'
      ? 'completed'
      : groupInfo?.status === 'CONFIRMED'
        ? 'confirmed'
        : 'open';

  return (
    <div className="app-shell chat-page">
      <aside className="app-sidebar">
        <img src="/images/logoTextWhite.png" alt="공구나라" className="app-sidebar-logo" />
      </aside>

      <main className="app-main">
        <div className="app-card chat-card">
          <div className="app-card-header chat-header">
            <button type="button" className="app-back" onClick={onBack}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span>뒤로</span>
            </button>
            <h2 className="app-card-title chat-title">
              {chatRoomName || `채팅방 ${chatRoomId}`}
            </h2>
            <button type="button" className="app-btn danger chat-leave" onClick={handleLeaveChatRoom}>
              나가기
            </button>
          </div>

          <div className="app-card-body chat-body">
            {postInfo && (
              <section className="chat-notice">
                <div className="chat-notice-title">{postInfo.title}</div>
                {postInfo.content && <p className="chat-notice-content">{postInfo.content}</p>}
                <div className="chat-notice-meta">
                  {postInfo.owner && <span>작성자 {postInfo.owner}</span>}
                  {postInfo.priceCents != null && (
                    <span>{Number(postInfo.priceCents).toLocaleString()}원</span>
                  )}
                  {postInfo.purchasePlace && <span>{postInfo.purchasePlace}</span>}
                  {postInfo.meetupPlace && <span>{postInfo.meetupPlace}</span>}
                  {postInfo.headcount != null && <span>{postInfo.headcount}명 모집</span>}
                </div>
                {groupInfo && (
                  <div className={`app-badge ${statusClass} chat-status`}>
                    {groupInfo.status === 'COMPLETED' && '공구 완료'}
                    {groupInfo.status === 'CONFIRMED' && `날짜 확정 · ${groupInfo.confirmedDate || '-'}`}
                    {groupInfo.status === 'OPEN' && '모집 중'}
                  </div>
                )}
              </section>
            )}

            {postInfo && groupInfo && (
              <GroupActions
                groupId={postInfo.groupId}
                username={username}
                isOwner={postInfo.owner === username}
                groupStatus={groupInfo.status}
                onUpdate={handleGroupUpdate}
              />
            )}

            <div className="chat-box">
              {messages.map((m, idx) => {
                const isMine = m.sender === username;
                return (
                  <div
                    key={m.id || `${m.createdAt}-${idx}`}
                    className={`chat-row ${isMine ? 'mine' : 'other'}`}
                  >
                    {!isMine && (
                      <button
                        type="button"
                        className="chat-sender"
                        onClick={() => onViewProfile?.(m.sender)}
                      >
                        {m.sender}
                      </button>
                    )}
                    <div className="chat-bubble-wrap">
                      {m.content && <div className="chat-bubble">{m.content}</div>}
                      {m.attachment && (
                        <img
                          src={m.attachment}
                          alt=""
                          className="chat-image"
                          onClick={() => window.open(m.attachment, '_blank')}
                        />
                      )}
                      <div className="chat-time">
                        {m.createdAt
                          ? new Date(m.createdAt).toLocaleTimeString('ko-KR', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>

            <div className="chat-composer">
              <div className="chat-composer-top">
                <label className="app-btn secondary chat-attach">
                  이미지
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => setImageFile(e.target.files[0] || null)}
                    hidden
                  />
                </label>
                {imageFile && <span className="chat-file-name">{imageFile.name}</span>}
              </div>
              <div className="chat-composer-row">
                <input
                  className="app-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={imageFile ? '메시지 (선택)' : '메시지를 입력하세요'}
                  onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                />
                <button type="button" className="app-btn" onClick={sendMessage}>
                  {imageFile ? '전송' : '전송'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {showReviewModal && postInfo && (
        <ReviewModal
          groupId={postInfo.groupId}
          username={username}
          isOwner={postInfo.owner === username}
          owner={postInfo.owner}
          participants={participants}
          onClose={() => setShowReviewModal(false)}
          onSubmit={() => {
            setShowReviewModal(false);
            alert('평가해주셔서 감사합니다!');
          }}
        />
      )}
    </div>
  );
}

export default ChatRoomPage;
